import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { PenLine, Coins, FileCheck2, TrendingUp, ArrowRight, ArrowUpRight } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const EXAM_LABELS = { upsc: "UPSC", state_psc: "State PSC", generic: "Generic" };

export default function Dashboard() {
  const { user } = useAuth();
  const [evals, setEvals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/evaluations").then(({ data }) => setEvals(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const done = evals.filter((e) => e.result && e.status !== "failed");
  const avg =
    done.length > 0
      ? (
          done.reduce((s, e) => s + (Number(e.result?.score) || 0) / (Number(e.result?.max_marks) || 10) * 10, 0) /
          done.length
        ).toFixed(1)
      : "—";

  const firstName = (user?.name || "there").split(" ")[0];

  const trend = done
    .slice()
    .reverse()
    .map((e, i) => ({
      idx: i + 1,
      date: new Date(e.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      score: Number((((Number(e.result?.score) || 0) / (Number(e.result?.max_marks) || 10)) * 10).toFixed(1)),
    }));

  return (
    <div className="min-h-screen bg-[#FDFDF9]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-5 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-[#2D6A4F] font-semibold">Dashboard</span>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-2">Hello, {firstName}.</h1>
            <p className="text-[#525252] mt-1">Ready to get another answer corrected?</p>
          </div>
          <Link to="/evaluate" data-testid="new-evaluation-btn">
            <Button className="rounded-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white h-12 px-6">
              <PenLine className="w-4 h-4 mr-1" /> New evaluation
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          <div data-testid="stat-credits" className="bg-white rounded-2xl border border-[#E5E5E5] p-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#525252]">Credits left</span>
              <Coins className="w-5 h-5 text-[#D97757]" />
            </div>
            <div className="font-display text-4xl mt-3">{user?.credits ?? 0}</div>
            <Link to="/buy-credits" className="inline-flex items-center gap-1 text-sm text-[#2D6A4F] font-semibold mt-2 hover:underline">
              Buy more <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div data-testid="stat-total" className="bg-white rounded-2xl border border-[#E5E5E5] p-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#525252]">Answers evaluated</span>
              <FileCheck2 className="w-5 h-5 text-[#2D6A4F]" />
            </div>
            <div className="font-display text-4xl mt-3">{evals.length}</div>
            <span className="text-sm text-[#525252] mt-2 block">Lifetime</span>
          </div>
          <div data-testid="stat-avg" className="bg-white rounded-2xl border border-[#E5E5E5] p-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#525252]">Average score</span>
              <TrendingUp className="w-5 h-5 text-[#2D6A4F]" />
            </div>
            <div className="font-display text-4xl mt-3">{avg}<span className="text-lg text-[#525252]">/10</span></div>
            <span className="text-sm text-[#525252] mt-2 block">Across all copies</span>
          </div>
        </div>

        {/* Progress chart */}
        {trend.length >= 2 && (
          <div className="mt-8 bg-white rounded-2xl border border-[#E5E5E5] p-6" data-testid="score-trend">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-display text-2xl tracking-tight">Your progress</h2>
                <p className="text-sm text-[#525252]">Normalised score (out of 10) across your evaluations</p>
              </div>
              <TrendingUp className="w-5 h-5 text-[#2D6A4F]" />
            </div>
            <div className="h-64" style={{ minHeight: 240 }}>
              <ResponsiveContainer width="100%" height="100%" minHeight={240}>
                <LineChart data={trend} margin={{ left: -18, right: 8, top: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEEEE8" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#525252" }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 10]} tick={{ fontSize: 12, fill: "#525252" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E5E5", fontSize: 13 }} />
                  <Line type="monotone" dataKey="score" stroke="#2D6A4F" strokeWidth={2.5} dot={{ r: 3, fill: "#2D6A4F" }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Recent */}
        <div className="mt-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl tracking-tight">Recent evaluations</h2>
            <Link to="/history" className="text-sm text-[#2D6A4F] font-semibold hover:underline">View all</Link>
          </div>

          {loading ? (
            <div className="text-[#525252]">Loading…</div>
          ) : evals.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-[#D6D6CE] p-12 text-center">
              <div className="w-14 h-14 rounded-2xl bg-[#EAF1ED] flex items-center justify-center mx-auto mb-4">
                <PenLine className="w-6 h-6 text-[#2D6A4F]" />
              </div>
              <h3 className="font-semibold text-lg">No evaluations yet</h3>
              <p className="text-[#525252] mt-1 mb-5">Upload your first handwritten answer to get mentor feedback.</p>
              <Link to="/evaluate">
                <Button className="rounded-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white">Evaluate an answer</Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {evals.slice(0, 4).map((e) => (
                <Link
                  key={e.id}
                  to={`/result/${e.id}`}
                  data-testid={`eval-card-${e.id}`}
                  className="group bg-white rounded-2xl border border-[#E5E5E5] p-6 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-200"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#2D6A4F] bg-[#EAF1ED] px-2.5 py-1 rounded-full">
                      {EXAM_LABELS[e.exam_type] || e.exam_type}
                    </span>
                    {e.result ? (
                      <span className="font-display text-2xl text-[#2D6A4F]">
                        {e.result?.score}<span className="text-sm text-[#525252]">/{e.result?.max_marks}</span>
                      </span>
                    ) : (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: e.status === "failed" ? "#FBEEE8" : "#EEF3F0", color: e.status === "failed" ? "#C0492B" : "#2D6A4F" }}>
                        {e.status === "failed" ? "Failed" : "Evaluating…"}
                      </span>
                    )}
                  </div>
                  <p className="text-[#1C1C1C] font-medium line-clamp-2">{e.question}</p>
                  <div className="flex items-center justify-between mt-4 text-sm text-[#525252]">
                    <span>{new Date(e.created_at).toLocaleDateString()}</span>
                    <span className="inline-flex items-center gap-1 text-[#2D6A4F] font-semibold group-hover:gap-2 transition-[gap]">
                      View <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
